export interface MLQuestion {
  id: number;
  short: string;
  question: string;
  company: string;
  difficulty: "Easy" | "Medium" | "Hard";
  answer: string;
}

export const questions: MLQuestion[] = [
  {
    id: 1,
    short: "Overfitting & regularization",
    question: "Explain overfitting and regularization",
    company: "Google",
    difficulty: "Easy",
    answer: `**Overfitting** happens when a model learns the noise of the training data and fails to generalize to new examples. **Regularization** adds constraints such as L1 or L2 penalties, dropout, or early stopping to discourage overly complex models and improve generalization.`
  },
  {
    id: 2,
    short: "FNN components",
    question: "Define the components of a Feedforward Neural Network (FNN)",
    company: "OpenAI",
    difficulty: "Easy",
    answer: `A feedforward neural network has an input layer, one or more hidden layers, and an output layer. Each neuron computes a weighted sum plus bias followed by an activation. Weights and biases are trained using backpropagation and gradient descent.`
  },
  {
    id: 3,
    short: "Activation functions",
    question: "What are activation functions & why do we need them?",
    company: "Meta",
    difficulty: "Medium",
    answer: `Activation functions apply a non-linear transformation after the linear step \(z=w^Tx+b\). Without them a deep network reduces to a linear model. They introduce non-linearity so networks can approximate complex functions and remain trainable.`
  },
  {
    id: 4,
    short: "Types of activations",
    question: "Types of Activation Functions",
    company: "Google",
    difficulty: "Medium",
    answer: `Common activations include ReLU, Sigmoid, Tanh, Leaky ReLU and Softmax. Each has advantages and trade-offs in terms of range, saturation and computational cost.`
  },
  {
    id: 5,
    short: "Bias-variance tradeoff",
    question: "Explain the bias-variance tradeoff.",
    company: "Meta",
    difficulty: "Easy",
    answer: `Bias measures error from erroneous assumptions while variance measures sensitivity to fluctuations in the training set. Increasing model complexity usually lowers bias but raises variance. The tradeoff is choosing a model that balances the two so total error is minimized.`
  },
  {
    id: 6,
    short: "Data imbalance",
    question: "How do you handle data imbalance issues?",
    company: "Amazon",
    difficulty: "Medium",
    answer: `Techniques include **resampling** (oversample the minority or undersample the majority), using **class weights** so rare classes count more in the loss, generating synthetic samples like SMOTE, and focusing on metrics such as precision, recall or F1 instead of accuracy.`
  },
  {
    id: 7,
    short: "GD vs SGD",
    question: "Explain Gradient descent and Stochastic gradient descent. Which one would you prefer?",
    company: "Apple",
    difficulty: "Medium",
    answer: `**Gradient descent** computes the gradient on the whole dataset each step, providing stable but potentially slow updates. **Stochastic gradient descent (SGD)** uses a single example or mini-batch, giving noisy but much faster updates that can escape shallow minima. SGD or mini-batch SGD is typically preferred for large datasets.`
  },
  {
    id: 8,
    short: "Logistic regression derivation",
    question: "Can you explain logistic regression and derive gradient descent for Logistic regression?",
    company: "Stripe",
    difficulty: "Hard",
    answer: `Logistic regression models $P(y=1|x)$ with the sigmoid $\sigma(w^Tx)$. The loss for one sample is $\ell=-y\log\sigma(w^Tx)-(1-y)\log(1-\sigma(w^Tx))$. The gradient with respect to $w$ is $(\sigma(w^Tx)-y)\,x$, which is used in gradient descent updates.`
  }
];
